import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { NeedDto, NeedsActionDto } from '@act/shared/data-transfer-objects';
import { Permission } from '@act/shared/authorization';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { getMemberNeeds, needsSelectors } from 'redux/slices/needs.slice';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { selectSelectedMemberId } from 'redux/slices/selectedMemberSlice.slice';
import { membersAppSelectors } from 'redux/slices/memberSlice.slice';
import syvaNeedsUtils from '../../syvaNeeds.util';

export interface NeedDetailState {
  need: NeedDto | undefined;
  issue: { id: number; label: string } | undefined;
  category: { id: number; label: string } | undefined;
  domain: { id: number; label: string } | undefined;
  daysRemaining: number;
  isEditable: boolean;
  hasAdminPermission: boolean;
  isAddActionOpen: boolean;
  setIsAddActionOpen: (open: boolean) => void;
  editAction: NeedsActionDto | null;
  setEditAction: (action: NeedsActionDto | null) => void;
  editNeed: NeedDto | null;
  setEditNeed: (need: NeedDto | null) => void;
  adminMenuAnchor: HTMLElement | null;
  setAdminMenuAnchor: (anchor: HTMLElement | null) => void;
  transferDialogOpen: boolean;
  setTransferDialogOpen: (open: boolean) => void;
  consolidateDialogOpen: boolean;
  setConsolidateDialogOpen: (open: boolean) => void;
  refreshNeeds: () => void;
  resetState: () => void;
}

export const useNeedDetailState = (needId: number): NeedDetailState => {
  const dispatch = useAppDispatch();
  const [isAddActionOpen, setIsAddActionOpen] = useState(false);
  const [editAction, setEditAction] = useState<NeedsActionDto | null>(null);
  const [editNeed, setEditNeed] = useState<NeedDto | null>(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [consolidateDialogOpen, setConsolidateDialogOpen] = useState(false);

  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const need = needsSelectors.useSelectNeedById(needId);
  const selectedMemberId = useAppSelector(selectSelectedMemberId);
  const member = membersAppSelectors.useSelectMemberById(selectedMemberId);

  const issue = needMetadata.data.issues.find(i => i.id === need?.issueId);
  const category = needMetadata.data.categories.find(
    c => c.id === issue?.categoryId
  );
  const domain = needMetadata.data.domains.find(
    d => d.id === category?.domainId
  );
  const daysRemaining = syvaNeedsUtils.calculateRemainingDays(
    need?.expiresAt || 0
  );
  const isEditable =
    need && member ? syvaNeedsUtils.needIsEditable(need, member) : false;
  const hasAdminPermission = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ACCESS_ADMIN_TOOLS
  );

  useEffect(() => {
    if (needId && !need && selectedMemberId) {
      dispatch(getMemberNeeds({ params: { memberId: selectedMemberId } }));
    }
  }, [needId, need, dispatch, selectedMemberId]);

  const refreshNeeds = () => {
    if (need?.memberId) {
      dispatch(getMemberNeeds({ params: { memberId: need.memberId } }));
    }
  };

  const resetState = () => {
    setIsAddActionOpen(false);
    setEditAction(null);
    setEditNeed(null);
    setAdminMenuAnchor(null);
    setTransferDialogOpen(false);
    setConsolidateDialogOpen(false);
  };

  return {
    need,
    issue,
    category,
    domain,
    daysRemaining,
    isEditable,
    hasAdminPermission,
    isAddActionOpen,
    setIsAddActionOpen,
    editAction,
    setEditAction,
    editNeed,
    setEditNeed,
    adminMenuAnchor,
    setAdminMenuAnchor,
    transferDialogOpen,
    setTransferDialogOpen,
    consolidateDialogOpen,
    setConsolidateDialogOpen,
    refreshNeeds,
    resetState,
  };
};
